import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { IRosterResponse } from '$models'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import { rosterService } from '$services'
import dayjs from 'dayjs'
import { RostersSlice } from '../slice'
import sortBy from 'lodash/sortBy'

interface RosterTemplatesState {
  showDeactivated: boolean
}

export interface RosterTemplatesSlice extends RosterTemplatesState {
  /**
   * Resets RosterTemplates Slice state
   */
  resetRosterTemplatesSlice: () => void

  setShowDeactivated: () => void

  /**
   * Create a Roster Template
   * @param successMessage Success Message to display if the creation was successful
   * @param errorMessage Error Message to display if the creation has failed
   * @returns
   */
  createRosterTemplate: (
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IRosterResponse | null>

  /**
   * Deactivate a Roster Template
   * @param rosterTemplateId to deactivate
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  deactivateOrRestoreRosterTemplate: (
    rosterTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IRosterResponse | null>

  /**
   * Copy a Roster Template
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  copyRosterTemplate: (
    rosterId: string,
    templateName: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IRosterResponse | null>
}

const initialState: RosterTemplatesState = {
  showDeactivated: false,
}

const createRosterTemplatesSlice: StateCreator<
  RosterTemplatesSlice & RostersSlice & SharedSlice,
  [],
  [],
  RosterTemplatesSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  resetRosterTemplatesSlice: () => set(initialState),

  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),

  // State API calls
  createRosterTemplate: async (
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const newRosterTemplate: IRosterResponse =
        await rosterService.createRosterTemplate()

      if (!newRosterTemplate) {
        logger.error(`${errorMessage} - newRosterTemplate doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return newRosterTemplate
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    }
  },

  deactivateOrRestoreRosterTemplate: async (
    rosterTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ) => {
    const { activeRosterTemplates, deactivatedRosterTemplates } = get()
    const rosterTemplate =
      action === 'deactivate'
        ? activeRosterTemplates.find(
            (template) => template.id === rosterTemplateId
          )
        : deactivatedRosterTemplates.find(
            (template) => template.id === rosterTemplateId
          )

    if (!rosterTemplate) {
      logger.error(`${errorMessage} - rosterTemplate doesn't exist`)
      enqueueLoadedSnackbar(errorMessage, 'error')
      return null
    }

    try {
      // We do an optimistic update of the rosterTemplates state so it is reflected in the UI
      // and if it fails we revert the state back to the original state
      if (action === 'deactivate') {
        // Move from active to deactivated
        set((state) => ({
          activeRosterTemplates: state.activeRosterTemplates.filter(
            (template) => template.id !== rosterTemplateId
          ),
          deactivatedRosterTemplates: sortBy<IRosterResponse>(
            [
              ...state.deactivatedRosterTemplates,
              { ...rosterTemplate, datestampDeleted: dayjs() },
            ],
            'templateName'
          ),
        }))
      } else {
        // Move from deactivated to active
        set((state) => ({
          deactivatedRosterTemplates: state.deactivatedRosterTemplates.filter(
            (template) => template.id !== rosterTemplateId
          ),
          activeRosterTemplates: sortBy<IRosterResponse>(
            [
              ...state.activeRosterTemplates,
              { ...rosterTemplate, datestampDeleted: undefined },
            ],
            'templateName'
          ),
        }))
      }

      rosterTemplate.datestampDeleted =
        action === 'deactivate' ? dayjs() : undefined
      const updatedRosterTemplate: IRosterResponse =
        await rosterService.updateRosterTemplate(rosterTemplate)

      if (!updatedRosterTemplate) {
        logger.error(`${errorMessage} - updatedRosterTemplate doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return updatedRosterTemplate
    } catch (error) {
      // Rollback the state
      if (action === 'deactivate') {
        // Rollback: move back from deactivated to active
        set((state) => ({
          deactivatedRosterTemplates: state.deactivatedRosterTemplates.filter(
            (template) => template.id !== rosterTemplateId
          ),
          activeRosterTemplates: sortBy<IRosterResponse>(
            [...state.activeRosterTemplates, rosterTemplate],
            'templateName'
          ),
        }))
      } else {
        // Rollback: move back from active to deactivated
        set((state) => ({
          activeRosterTemplates: state.activeRosterTemplates.filter(
            (template) => template.id !== rosterTemplateId
          ),
          deactivatedRosterTemplates: sortBy<IRosterResponse>(
            [...state.deactivatedRosterTemplates, rosterTemplate],
            'templateName'
          ),
        }))
      }
      exceptionMessageHandler(error, errorMessage)
      return null
    }
  },

  copyRosterTemplate: async (
    rosterId: string,
    templateName: string,
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoading: true }))
    try {
      const copiedRosterTemplate: IRosterResponse =
        await rosterService.copyRosterTemplate(rosterId, templateName)

      if (!copiedRosterTemplate) {
        logger.error(`${errorMessage} - addedStaffMember doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      set((state) => ({
        activeRosterTemplates: sortBy<IRosterResponse>(
          [...state.activeRosterTemplates, copiedRosterTemplate],
          'templateName'
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      return copiedRosterTemplate
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createRosterTemplatesSlice
